import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from '../../context/AuthContext';
import { useTranslations } from "../../hooks/useTranslations";
import useTemplatesHook from '../../services/twilio/useTemplatesHook';
import { useBusinessConfig } from '../../context/BusinessConfigContext';
import campaignService from '../../services/campaignService';

export default function CampaignCard({ campaign, onEdit, onDelete, onLaunchCampaign, onUpdateCampaign }) {
  const t = useTranslations();
  const { user } = useAuth();
  const { config } = useBusinessConfig();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);

  // Calculate expected credit cost
  const costPerMessage = config?.costPerMessage || 0.20;
  const customerCount = campaign.customers?.length || 0;
  const expectedCreditCost = costPerMessage * customerCount;
  const availableCredits = config?.credits || 0;

  const handleFinalLaunch = async () => {
    // Check if user has enough credits
    if (expectedCreditCost > availableCredits) {
      const shortfall = (expectedCreditCost - availableCredits).toFixed(2);
      alert(
        `אין מספיק קרדיט להשקת הקמפיין.\n\n` +
        `קרדיט זמין: ${availableCredits.toFixed(2)}₪\n` +
        `עלות נדרשת: ${expectedCreditCost.toFixed(2)}₪\n` +
        `חסר: ${shortfall}₪\n\n` +
        `אנא טען קרדיט נוסף כדי להשיק את הקמפיין.`
      );
      return;
    }

    if (window.confirm(`השק את הקמפיין "${campaign.name}"?`)) {
      try {
        const result = await onLaunchCampaign?.(campaign);
        if (result?.success) {
          setLaunchResult(result.result);
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error('Launch failed:', error);
        // Show user-friendly error message
        alert(`שגיאה בהשקת הקמפיין: ${error.message}`);
      }
    }
  };

  const PaymentButton = () => {
    const hasInsufficientCredits = expectedCreditCost > availableCredits;

    const getButtonConfig = () => {
      return {
        text: hasInsufficientCredits ? 'אין מספיק קרדיט' : 'השק עכשיו!',
        disabled: hasInsufficientCredits,
        className: hasInsufficientCredits
          ? 'px-10 bg-gray-400 text-white cursor-not-allowed'
          : 'px-10 bg-green-600 text-white hover:bg-green-700',
        onClick: handleFinalLaunch,
        showSpinner: false,
        showCancel: false
      };
    };

    const config = getButtonConfig();

    return (
      <div className="flex flex-col gap-2">
        <button
          onClick={config.onClick}
          disabled={config.disabled}
          className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${config.className}`}
        >
          {config.showSpinner && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {config.text}
        </button>
      </div>
    );
  };

  const handleEdit = () => onEdit?.(campaign);

  const handleDelete = () => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את הקמפיין "${campaign.name}"?`)) {
      onDelete?.(campaign.id);
    }
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshAudience = async () => {
    if (!campaign.sqlQuery || isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      // Execute the stored query to get fresh customer data
      const result = await campaignService.executeStoredQuery(campaign.sqlQuery);

      if (result.success) {
        // Optimize customers for storage
        const optimizedCustomers = result.customers.map(customer => ({
          id: customer.id,
          full_name: customer.full_name || customer.customerName || 'לקוח יקר',
          phone: customer.phone
        })).filter(customer => customer.id && customer.phone);

        // Update campaign directly without opening modal
        if (onUpdateCampaign) {
          await onUpdateCampaign(campaign.id, {
            customers: optimizedCustomers
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing audience:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getTemplateById = (templateId) => {
    return templates.find(t => t.machineName === templateId) || null;
  };

  const getTemplateName = (templateId) => {
    const template = getTemplateById(templateId);
    return template?.displayName || template?.machineName || 'תבנית לא נמצאה';
  };

  const isTemplateApproved = (templateId) => {
    const template = getTemplateById(templateId);
    return template?.status === 'approved';
  };

  const { templates } = useTemplatesHook();
  const canLaunch = isTemplateApproved(campaign.templateId);

  return (
    <>
      <article
        role="group"
        aria-label={`${t.campaign} ${campaign.name}`}
        className="relative isolate rounded-2xl border border-zinc-200/70 bg-white shadow-sm  transition hover:shadow-md"
      >
        {/* Subtle gradient accent */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-amber-400 via-rose-300 to-sky-400 opacity-60" />

        <div className="flex flex-col gap-3 p-4">
          {/* Top Row: Execution status with badges - moved to top left */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <ExecutionStatusCompact executionStatus={campaign.executionStatus} />

            {/* Action buttons at top right */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleEdit}
                className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                title="ערוך קמפיין"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                ערוך
              </button>
              {campaign.sqlQuery && (
                <button
                  onClick={handleRefreshAudience}
                  disabled={isRefreshing}
                  className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition ${isRefreshing
                      ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                    }`}
                  title="רענן קהל יעד"
                >
                  {isRefreshing ? (
                    <>
                      <svg className="h-3.5 w-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      מרענן...
                    </>
                  ) : (
                    <>
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      רענן קהל
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 transition hover:bg-red-100"
                title="מחק קמפיין"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                מחק
              </button>
            </div>
          </div>

          {/* Campaign Title and Details */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-semibold text-zinc-900 sm:text-lg">
                {campaign.name}
              </h3>
              <p className="mt-1 text-sm text-zinc-600">
                {t.target} {campaign.targetAudience}
              </p>

              {/* Template name */}
              <div className="mt-2 flex items-center gap-1 text-xs text-zinc-500">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <span>תבנית: {getTemplateName(campaign.templateId)}</span>
              </div>

              {/* Customer count and expected credit cost */}
              {customerCount > 0 && (
                <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {customerCount} לקוחות
                  </span>
                  <span className="flex items-center gap-1 font-medium text-pink-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {expectedCreditCost.toFixed(2)}₪ עלות צפויה
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Launch Button and Warnings */}
          <div className="flex flex-col w-full gap-2">
            <div className='mx-auto'>
              <PaymentButton />
              {!canLaunch && (
                <div className="text-xs text-amber-600 flex items-center gap-1">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span>תבנית ממתינה לאישור</span>
                </div>
              )}
              {expectedCreditCost > availableCredits && (
                <div className="text-xs text-red-600 flex items-center gap-1">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span>אין מספיק קרדיט. נדרש: {expectedCreditCost.toFixed(2)}₪</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>
      {/* Success Modal */}
      {showSuccessModal && launchResult && createPortal(
        <CampaignSuccessModal
          messagesSent={launchResult.messagesSent}
          onClose={() => setShowSuccessModal(false)}
        />,
        document.body
      )}
    </>
  );
}

/* --------------------------------- Parts --------------------------------- */

function Badge({ children, tone = "neutral", title }) {
  const toneMap = {
    neutral: "bg-zinc-100 text-zinc-700 ring-1 ring-inset ring-zinc-200",
    info: "bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200",
    success: "bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200",
    warning: "bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200",
    danger: "bg-rose-100 text-rose-900 ring-1 ring-inset ring-rose-200",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${toneMap[tone]}`}
    >
      {children}
    </span>
  );
}

function ExecutionStatusCompact({ executionStatus }) {
  if (!executionStatus) return null;

  const { status, lastRun, messagesSent, messagesFailed } = executionStatus;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("he-IL");
    } catch (e) {
      return dateString;
    }
  };

  // Show last dispatch date and badges in a compact format
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Last dispatch date */}
      {status !== "never-ran" && lastRun && (
        <span className="text-xs text-zinc-500">
          רץ לאחרונה: {formatDate(lastRun)}
        </span>
      )}

      {/* Success/Failure badges */}
      {status === "completed" && (
        <>
          <Badge tone="success">
            {messagesSent} נשלחו
          </Badge>
          {messagesFailed > 0 && (
            <Badge tone="danger">{messagesFailed} נכשלו</Badge>
          )}
        </>
      )}

      {status === "failed" && (
        <Badge tone="danger">נכשל</Badge>
      )}

      {status === "running" && (
        <Badge tone="info">רץ כעת</Badge>
      )}

      {status === "never-ran" && (
        <Badge tone="neutral">לא רץ עדיין</Badge>
      )}
    </div>
  );
}

/* ------------------------------- Success Modal ------------------------------ */

function CampaignSuccessModal({ messagesSent, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Success Icon */}
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center">
          <h3 className="mb-2 text-2xl font-bold text-gray-900">
            הקמפיין רץ בהצלחה!
          </h3>
          <p className="text-lg text-gray-700">
            נשלח ל-<span className="font-bold text-green-600">{messagesSent}</span> אנשים
          </p>
        </div>

        {/* Confetti/Celebration Icon */}
        <div className="mt-4 flex justify-center text-4xl">
          🎉
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="mt-6 w-full rounded-lg bg-green-600 px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        >
          סגור
        </button>
      </div>
    </div>
  );
}
